import type { ReactNode } from 'react'

import estilos from './Indicador.module.css'

type Tono = 'neutro' | 'ok' | 'aviso' | 'alarma'

type Props = {
  rotulo: string
  /** La cifra, ya formateada. Nulo cuando todavía no hay con qué calcularla. */
  valor: string | null
  /** Una línea corta que dice de dónde sale o contra qué se compara. */
  detalle?: ReactNode
  tono?: Tono
  /**
   * AL DÍA EN VEZ DE UN CERO. Johan, mirando «0 · — · 0 · 0»: «¿esto qué me
   * dice? ¿no tiene datos?». Un cero grande se lee como algo que no cargó.
   * Cuando el cero es la buena noticia, se dice con palabras.
   */
  alDia?: boolean
}

/**
 * Una cifra del tablero.
 *
 * Va dentro del racimo —no es una tarjeta suelta— porque estas cinco cifras se
 * leen juntas: son el estado de la operación en una línea.
 *
 * LA CIFRA ES EL CONTENIDO, no un texto más. Va en la monoespaciada de las
 * mediciones, grande y apretada, para que se lea de lejos y de reojo. El
 * rótulo se retira a versalita pequeña y el contexto a una línea gris: los
 * tres compiten si tienen el mismo peso, y entonces no se lee ninguno.
 *
 * EL COLOR ENTRA POR ARRIBA Y EN LA CIFRA, NUNCA DE FONDO. Antes un indicador
 * en aviso se pintaba entero de durazno; con cuatro de cinco así, la pantalla
 * gritaba por todo y no señalaba nada. Ahora el tono es una regla de dos
 * píxeles y el color del número.
 */

/**
 * Separa el número de su unidad.
 *
 * "26,3 h" son dos cosas: la medición y qué se midió. Con el mismo tamaño, la
 * unidad le roba peso al número; más pequeña y en gris, el ojo va derecho a la
 * cifra y confirma la unidad después, que es el orden en que se lee.
 */
function partirCifra(valor: string): { numero: string; unidad: string | null } {
  const corte = valor.lastIndexOf(' ')

  if (corte === -1) return { numero: valor, unidad: null }

  const cola = valor.slice(corte + 1)

  // Solo es unidad si no tiene dígitos: "1.250 000" no se parte.
  return /\d/.test(cola)
    ? { numero: valor, unidad: null }
    : { numero: valor.slice(0, corte), unidad: cola }
}

export function Indicador({ rotulo, valor, detalle, tono = 'neutro', alDia = false }: Props) {
  const { numero, unidad } = valor ? partirCifra(valor) : { numero: '—', unidad: null }

  return (
    <article className={`${estilos.zona} ${estilos[tono]}`}>
      <p className={estilos.rotulo}>{rotulo}</p>

      {alDia ? (
        <p className={`${estilos.cifra} ${estilos.alDia}`}>
          <span className={estilos.visto} aria-hidden="true">
            ✓
          </span>
          Al día
        </p>
      ) : (
        <p className={estilos.cifra}>
          {numero}
          {unidad && <span className={estilos.unidad}>{unidad}</span>}
        </p>
      )}

      {detalle && <p className={estilos.detalle}>{detalle}</p>}
    </article>
  )
}
